import { useTranslation } from 'react-i18next';
import { Play, Pause, SkipBack, SkipForward, Undo2, Redo2, X } from 'lucide-react';
import { usePlayback } from '../../hooks/usePlayback';
import { useEditorStore } from '../../store/editorStore';
import { usePlayheadStore } from '../../store/playheadStore';
import { useLoopStore } from '../../store/loopStore';
import { msToTimecode } from '../../lib/timeUtils';

export function MobileTransportBar() {
  const { t } = useTranslation();
  const { playing, currentTimeMs, togglePlayback, stepForward, stepBackward } = usePlayback();
  const fps = useEditorStore((s) => s.project.fps);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  const canUndo = useEditorStore((s) => s.canUndo());
  const canRedo = useEditorStore((s) => s.canRedo());
  const hasClips = useEditorStore((s) => s.project.tracks.some((tr) => tr.clips.length > 0));
  const selectionStart = useLoopStore((s) => s.selectionStart);
  const selectionEnd = useLoopStore((s) => s.selectionEnd);
  const hasSelection = selectionStart !== null && selectionEnd !== null;

  const handleSetIn = () => {
    const time = usePlayheadStore.getState().currentTimeMs;
    const end = selectionEnd ?? useEditorStore.getState().project.durationMs;
    useLoopStore.getState().setSelection(time, Math.max(time, end));
  };

  const handleSetOut = () => {
    const time = usePlayheadStore.getState().currentTimeMs;
    const start = selectionStart ?? 0;
    useLoopStore.getState().setSelection(Math.min(start, time), time);
  };

  const handleClearSelection = () => {
    useLoopStore.getState().clearSelection();
  };

  const btnClass =
    'p-2 rounded-full hover:bg-white/10 text-[var(--user-page-text)] opacity-70 hover:opacity-100 transition-all disabled:opacity-30';

  const regionBtnClass =
    'px-2 py-1 rounded-md text-[10px] font-bold tracking-wide transition-colors';

  return (
    <div className="shrink-0 bg-[#141416] border-y border-[#2A2A2E]">
      {/* Main transport row */}
      <div className="flex items-center justify-between px-2 py-1">
        {/* Left: undo/redo */}
        <div className="flex items-center gap-0.5">
          <button onClick={undo} disabled={!canUndo} className={btnClass} aria-label={t('editor.undo', 'Undo')}>
            <Undo2 size={18} />
          </button>
          <button onClick={redo} disabled={!canRedo} className={btnClass} aria-label={t('editor.redo', 'Redo')}>
            <Redo2 size={18} />
          </button>
        </div>

        {/* Center: transport */}
        <div className="flex items-center gap-1">
          <button onClick={stepBackward} disabled={!hasClips} className={btnClass} aria-label={t('editor.stepBack', 'Step back')}>
            <SkipBack size={18} />
          </button>
          <button
            onClick={togglePlayback}
            disabled={!hasClips}
            className="p-3 rounded-full bg-[var(--user-page-accent)] text-[var(--user-page-bg)] hover:brightness-110 transition-all disabled:opacity-30"
            aria-label={playing ? t('editor.pause', 'Pause') : t('editor.play', 'Play')}
          >
            {playing ? <Pause size={22} /> : <Play size={22} />}
          </button>
          <button onClick={stepForward} disabled={!hasClips} className={btnClass} aria-label={t('editor.stepForward', 'Step forward')}>
            <SkipForward size={18} />
          </button>
        </div>

        {/* Right: timecode */}
        <div className="font-mono text-[11px] text-[var(--user-page-text)] opacity-60 tabular-nums min-w-[72px] text-end">
          {msToTimecode(currentTimeMs, fps)}
        </div>
      </div>

      {/* Region selection row */}
      <div className="flex items-center justify-center gap-2 px-3 pb-1.5">
        <button
          onClick={handleSetIn}
          disabled={!hasClips}
          className={`${regionBtnClass} ${
            selectionStart !== null
              ? 'bg-[var(--user-page-accent)]/20 text-[var(--user-page-accent)] border border-[var(--user-page-accent)]/40'
              : 'bg-white/5 text-[#8E8E93] border border-white/10'
          }`}
        >
          {selectionStart !== null
            ? `IN ${msToTimecode(selectionStart, fps).slice(3)}`
            : t('editor.loopIn', 'Loop In')}
        </button>

        <button
          onClick={handleSetOut}
          disabled={!hasClips}
          className={`${regionBtnClass} ${
            selectionEnd !== null
              ? 'bg-[var(--user-page-accent)]/20 text-[var(--user-page-accent)] border border-[var(--user-page-accent)]/40'
              : 'bg-white/5 text-[#8E8E93] border border-white/10'
          }`}
        >
          {selectionEnd !== null
            ? `OUT ${msToTimecode(selectionEnd, fps).slice(3)}`
            : t('editor.loopOut', 'Loop Out')}
        </button>

        {hasSelection && (
          <button
            onClick={handleClearSelection}
            className="p-1 rounded-full hover:bg-white/10 text-[#8E8E93] transition-colors"
            aria-label={t('editor.mobile.clearSelection', 'Clear selection')}
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
